/**
 * The **filter builder** — one of the two shared egress/ingress form patterns
 * (storyboard ``shared/form-patterns.md``; ADR-036 §D-4).
 *
 * Authors the per-interface predicate: a small boolean tree of selector clauses
 * over producer / aggregator / message type / geographic area / SAPIENT message
 * fields. Renders recursively — a composite row holds its children indented
 * beneath it — because the document model nests without a depth bound and a
 * flattened editor would silently refuse trees the server accepts.
 *
 * # Default-deny is stated, never implied
 *
 * The document is fail-safe default-deny (REQ-PEER-033) and **cannot express
 * "allow everything"** — an interface forwarding the full firehose binds
 * ``AllowAllFilter`` in code, which keeps that a reviewable decision. From the
 * outside, "no filter yet" and "a filter that matches nothing" look identical:
 * the interface is silent either way. So the builder says which one it is, in
 * words, above the tree (:func:`describeDenyReason`) rather than leaving an
 * operator to infer it from an empty panel. This is the same discipline as
 * never painting an unknown component green.
 *
 * # Read-only is a rendering mode, not a disabled form
 *
 * ``readOnly`` renders the tree without edit affordances. A non-privileged
 * session never reaches this component at all — the BFF withholds the filter
 * literals entirely and the surface shows the summary instead — so ``readOnly``
 * is for the privileged-but-not-editing case (reviewing before a save).
 *
 * Cross-references:
 *   - ADR-036 §D-4; REQ-PEER-031 / -032 / -033.
 *   - Flow W §5 (define the filter); ``shared/form-patterns.md`` §2.
 *   - REQ-UI-059 (keyboard + labelled controls); REQ-UI-040 (both themes).
 */
import { useMemo, type ReactNode } from "react";
import {
  ALERT_PRIORITIES,
  ALERT_TYPES,
  LOCATION_SOURCES,
  MESSAGE_TYPES,
} from "@/peers/peer-catalog";
import {
  CLAUSE_LABELS,
  COMPOSITE_CLAUSE_KINDS,
  LEAF_CLAUSE_KINDS,
  appendClause,
  canonicalizeClause,
  clauseAppliesTo,
  countLeaves,
  describeDenyReason,
  isComposite,
  newClause,
  removeClause,
  replaceClause,
  type ClausePath,
  type CompositeClauseKind,
  type LeafClauseKind,
} from "@/peers/peer-filter-tree";
import {
  PeerCheckboxSet,
  PeerNumberInput,
  PeerSelect,
  PeerTokenList,
} from "@/peers/peer-form-primitives";
import type { ClauseNode, CompositeNode } from "@/peers/peer-model";

/** Props for {@link PeerFilterBuilder}. */
export interface PeerFilterBuilderProps {
  /** The current root predicate, or ``null`` (denies everything). */
  root: ClauseNode | null;
  onChange: (next: ClauseNode | null) => void;
  /** Greys out the SAPIENT-only selectors for a raw interface. */
  payloadDomain: "sapient" | "raw";
  readOnly?: boolean;
  /**
   * The direction's own statement about what this document means, rendered above
   * the tree.
   *
   * ``undefined`` (the default) renders the **egress** deny notice — the reading
   * this component was written for. The ingress surface passes its own, because
   * the identical document means the opposite thing there: no filter *receives
   * everything* rather than emitting nothing (REQ-UI-160). The clause tree below
   * is direction-neutral and is shared verbatim; only this sentence is not
   * (CLAUDE.md §4.4).
   *
   * Pass ``null`` to render no statement at all — which no caller should want on
   * either surface, since the whole point is that an empty panel says nothing.
   */
  notice?: ReactNode;
  /** Scopes the container's test id, so the two surfaces are separately addressable. */
  testId?: string;
}

/** Render the filter builder for one interface. */
export function PeerFilterBuilder({
  root,
  onChange,
  payloadDomain,
  readOnly = false,
  notice,
  testId = "peer-filter-builder",
}: PeerFilterBuilderProps): React.ReactElement {
  const denyReason = useMemo(() => describeDenyReason(root), [root]);
  const leafCount = countLeaves(canonicalizeClause(root));

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="text-meta text-text-secondary">
          {leafCount} {leafCount === 1 ? "condition" : "conditions"}
        </span>
        {!readOnly && root === null && (
          <div className="ml-auto flex gap-1">
            <AddButton
              label="Add condition"
              testId="filter-add-root-leaf"
              onAdd={() => onChange(newClause("message_type"))}
            />
            <AddButton
              label="Add group"
              testId="filter-add-root-group"
              onAdd={() => onChange(newClause("all_of"))}
            />
          </div>
        )}
      </div>

      {notice !== undefined
        ? notice
        : denyReason !== null && (
            <p
              role="status"
              data-testid="filter-deny-notice"
              className="rounded border border-warning/40 bg-warning/10 px-2 py-1.5 text-meta text-warning"
            >
              {denyReason}
            </p>
          )}

      {root !== null && (
        <ClauseEditor
          node={root}
          path={[]}
          payloadDomain={payloadDomain}
          readOnly={readOnly}
          onReplace={(path, next) => onChange(replaceClause(root, path, next))}
          onRemove={(path) => onChange(removeClause(root, path))}
          onAppend={(path, child) => onChange(appendClause(root, path, child))}
        />
      )}
    </div>
  );
}

interface ClauseEditorProps {
  node: ClauseNode;
  path: ClausePath;
  payloadDomain: "sapient" | "raw";
  readOnly: boolean;
  onReplace: (path: ClausePath, next: ClauseNode) => void;
  onRemove: (path: ClausePath) => void;
  onAppend: (path: ClausePath, child: ClauseNode) => void;
}

/** One clause row — a composite group with nested children, or a leaf selector. */
function ClauseEditor(props: ClauseEditorProps): React.ReactElement {
  const { node, path, readOnly, onRemove } = props;
  const testId = `filter-clause-${path.join("-") || "root"}`;
  return (
    <div
      data-testid={testId}
      className="rounded border border-border bg-bg-base p-2"
      // A group is a real ARIA group so its label announces before its children
      // — the tree's shape is the predicate's meaning (REQ-UI-059).
      role={isComposite(node) ? "group" : undefined}
      aria-label={isComposite(node) ? CLAUSE_LABELS[node.kind] : undefined}
    >
      <div className="flex items-center gap-2">
        <span className="text-meta font-medium uppercase tracking-wide text-text-secondary">
          {CLAUSE_LABELS[node.kind]}
        </span>
        {!readOnly && (
          <button
            type="button"
            onClick={() => onRemove(path)}
            aria-label={`Remove ${CLAUSE_LABELS[node.kind]} condition`}
            data-testid={`${testId}-remove`}
            className="ml-auto rounded border border-border px-1.5 text-meta text-text-secondary hover:border-destructive hover:text-destructive"
          >
            Remove
          </button>
        )}
      </div>
      <div className="pt-2">
        {isComposite(node) ? (
          <CompositeBody {...props} node={node} />
        ) : (
          <LeafBody {...props} node={node} />
        )}
      </div>
    </div>
  );
}

/** A composite's children, indented, plus its own add affordances. */
function CompositeBody({
  node,
  path,
  payloadDomain,
  readOnly,
  onReplace,
  onRemove,
  onAppend,
}: ClauseEditorProps & { node: CompositeNode }): React.ReactElement {
  return (
    <div className="flex flex-col gap-2">
      {!readOnly && (
        <PeerSelect
          label="Match"
          value={node.kind}
          testId={`filter-clause-${path.join("-") || "root"}-mode`}
          options={COMPOSITE_CLAUSE_KINDS.map((kind) => ({
            value: kind,
            label: kind === "all_of" ? "All of these (AND)" : "Any of these (OR)",
          }))}
          onChange={(value) =>
            onReplace(path, { ...node, kind: value as CompositeClauseKind } as CompositeNode)
          }
        />
      )}
      <div className="flex flex-col gap-2 border-l border-border pl-3">
        {node.clauses.length === 0 ? (
          <p className="text-meta text-text-muted">
            Empty group — matches nothing until a condition is added.
          </p>
        ) : (
          node.clauses.map((child, index) => (
            <ClauseEditor
              key={index}
              node={child}
              path={[...path, index]}
              payloadDomain={payloadDomain}
              readOnly={readOnly}
              onReplace={onReplace}
              onRemove={onRemove}
              onAppend={onAppend}
            />
          ))
        )}
      </div>
      {!readOnly && (
        <div className="flex flex-wrap gap-1">
          <ClauseKindPicker
            payloadDomain={payloadDomain}
            testId={`filter-clause-${path.join("-") || "root"}-add`}
            onPick={(kind) => onAppend(path, newClause(kind))}
          />
        </div>
      )}
    </div>
  );
}

/** The leaf editors, one per selector kind. */
function LeafBody({
  node,
  path,
  payloadDomain,
  readOnly,
  onReplace,
}: ClauseEditorProps): React.ReactElement {
  const set = (next: ClauseNode): void => onReplace(path, next);
  const inapplicable = !clauseAppliesTo(
    node.kind as LeafClauseKind | CompositeClauseKind,
    payloadDomain,
  );

  if (readOnly) {
    return (
      <p className="font-mono text-body-sm text-text-primary">{JSON.stringify(stripKind(node))}</p>
    );
  }

  return (
    <>
      {inapplicable && (
        <p role="status" className="pb-1 text-meta text-warning">
          A raw interface carries blobs, not SAPIENT messages — this condition can only ever
          decline, silencing the flow.
        </p>
      )}
      {node.kind === "producer" && (
        <PeerTokenList
          label="Producer (sensor) ids"
          values={node.producer_ids}
          onChange={(producer_ids) => set({ ...node, producer_ids })}
          placeholder="BD-CAM-01"
          testId="filter-producer-input"
          hint="Matched exactly — ids are not case-folded."
        />
      )}
      {node.kind === "aggregator" && (
        <PeerTokenList
          label="Aggregator ids"
          values={node.aggregator_ids}
          onChange={(aggregator_ids) => set({ ...node, aggregator_ids })}
          placeholder="BD-AGG-01"
          testId="filter-aggregator-input"
        />
      )}
      {node.kind === "message_type" && (
        <PeerCheckboxSet
          label="Message types"
          options={MESSAGE_TYPES}
          selected={node.message_types}
          testIdPrefix="filter-message-type"
          onChange={(next) => set({ ...node, message_types: next as typeof node.message_types })}
        />
      )}
      {node.kind === "classification" && (
        <PeerTokenList
          label="Detection classification types"
          values={node.classification_types}
          onChange={(classification_types) => set({ ...node, classification_types })}
          placeholder="Human"
          testId="filter-classification-input"
          hint="Sensor-authored free text, matched exactly. List every spelling you need."
        />
      )}
      {node.kind === "alert_type" && (
        <PeerCheckboxSet
          label="Alert types"
          options={ALERT_TYPES}
          selected={node.alert_types}
          testIdPrefix="filter-alert-type"
          onChange={(alert_types) => set({ ...node, alert_types })}
        />
      )}
      {node.kind === "alert_priority" && (
        <PeerCheckboxSet
          label="Alert priorities"
          options={ALERT_PRIORITIES}
          selected={node.priorities}
          testIdPrefix="filter-alert-priority"
          onChange={(priorities) => set({ ...node, priorities })}
          hint="An allow-set, not a threshold — BSI priority ordering is not a standard guarantee."
        />
      )}
      {node.kind === "minimum_confidence" && (
        <PeerNumberInput
          label="Minimum confidence"
          value={node.minimum}
          min={0}
          max={1}
          step="any"
          testId="filter-confidence-input"
          onChange={(minimum) => set({ ...node, minimum })}
          hint="0–1. A record carrying no confidence declines."
        />
      )}
      {node.kind === "geo" && (
        <div className="flex flex-col gap-2">
          <PeerSelect
            label="Location source"
            value={node.source}
            testId="filter-geo-source"
            options={LOCATION_SOURCES.map((s) => ({ value: s.value, label: s.label }))}
            onChange={(source) => set({ ...node, source: source as typeof node.source })}
            hint={
              LOCATION_SOURCES.find((s) => s.value === node.source)?.hint ??
              "A record that does not carry this location never matches."
            }
          />
          <div className="grid grid-cols-2 gap-2">
            <PeerNumberInput
              label="Min latitude"
              value={node.min_latitude_deg}
              min={-90}
              max={90}
              step="any"
              testId="filter-geo-min-lat"
              onChange={(min_latitude_deg) => set({ ...node, min_latitude_deg })}
            />
            <PeerNumberInput
              label="Max latitude"
              value={node.max_latitude_deg}
              min={-90}
              max={90}
              step="any"
              testId="filter-geo-max-lat"
              onChange={(max_latitude_deg) => set({ ...node, max_latitude_deg })}
            />
            <PeerNumberInput
              label="Min longitude"
              value={node.min_longitude_deg}
              min={-180}
              max={180}
              step="any"
              testId="filter-geo-min-lon"
              onChange={(min_longitude_deg) => set({ ...node, min_longitude_deg })}
            />
            <PeerNumberInput
              label="Max longitude"
              value={node.max_longitude_deg}
              min={-180}
              max={180}
              step="any"
              testId="filter-geo-max-lon"
              onChange={(max_longitude_deg) => set({ ...node, max_longitude_deg })}
            />
          </div>
        </div>
      )}
    </>
  );
}

/** The clause-kind picker rendered inside a composite's add row. */
function ClauseKindPicker({
  payloadDomain,
  onPick,
  testId,
}: {
  payloadDomain: "sapient" | "raw";
  onPick: (kind: LeafClauseKind | CompositeClauseKind) => void;
  testId: string;
}): React.ReactElement {
  return (
    <label className="flex items-center gap-1 text-meta text-text-secondary">
      Add
      <select
        value=""
        data-testid={testId}
        aria-label="Add a condition or group"
        onChange={(event) => {
          const value = event.target.value;
          if (value !== "") onPick(value as LeafClauseKind | CompositeClauseKind);
          event.target.value = "";
        }}
        className="rounded border border-border bg-bg-base px-2 py-1 text-body-sm text-text-primary focus:outline-none focus:border-brand"
      >
        <option value="">Choose…</option>
        {LEAF_CLAUSE_KINDS.map((kind) => (
          <option key={kind} value={kind} disabled={!clauseAppliesTo(kind, payloadDomain)}>
            {CLAUSE_LABELS[kind]}
          </option>
        ))}
        {COMPOSITE_CLAUSE_KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {CLAUSE_LABELS[kind]} (group)
          </option>
        ))}
      </select>
    </label>
  );
}

function AddButton({
  label,
  onAdd,
  testId,
}: {
  label: string;
  onAdd: () => void;
  testId: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onAdd}
      data-testid={testId}
      className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
    >
      {label}
    </button>
  );
}

/** Drop the discriminator from the read-only JSON echo — the row header already says it. */
function stripKind(node: ClauseNode): Record<string, unknown> {
  const { kind: _kind, ...rest } = node;
  return rest;
}
